import { useEffect, useId, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { searchLegalSections } from "@/legal/legalSearch";

/**
 * Busca editorial local em um documento legal estruturado.
 * Não conhece Termos/Privacidade — recebe o documento por props.
 *
 * @param {{
 *   document: import("@/legal/legalDocument").LegalDocumentContent,
 *   onSelectSection: (id: string) => void,
 *   id?: string,
 *   className?: string,
 * }} props
 */
export function LegalSearch({
  document,
  onSelectSection,
  id,
  className,
}) {
  const generatedId = useId();
  const inputId = id ?? `legal-search-${generatedId}`;
  const listId = `${inputId}-results`;
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const trimmed = query.trim();
  const results = useMemo(
    () => searchLegalSections(query, document),
    [query, document],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const showResults = Boolean(trimmed);
  const activeResult = results[activeIndex] ?? null;

  function selectSection(idValue) {
    if (!idValue) return;
    onSelectSection(idValue);
    setQuery("");
  }

  function onKeyDown(event) {
    if (!showResults) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (results.length === 0) return;
      setActiveIndex((index) => (index + 1) % results.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (results.length === 0) return;
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (activeResult) {
        selectSection(activeResult.id);
      }
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setQuery("");
    }
  }

  return (
    <div className={cn("w-full", className)} data-testid="legal-search">
      <label htmlFor={inputId} className="sr-only">
        Buscar neste documento
      </label>
      <div className="relative w-full">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-500"
          aria-hidden
        />
        <input
          id={inputId}
          type="search"
          role="combobox"
          value={query}
          autoComplete="off"
          placeholder="Buscar neste documento..."
          aria-autocomplete="list"
          aria-expanded={showResults}
          aria-controls={showResults && results.length > 0 ? listId : undefined}
          aria-activedescendant={
            showResults && activeResult ? `${listId}-${activeResult.id}` : undefined
          }
          data-testid="legal-search-input"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          className="h-14 w-full rounded-2xl border border-zinc-800 bg-zinc-900/50 pl-12 pr-4 text-base text-white placeholder:text-zinc-500 transition-colors focus:border-zinc-600 focus:outline-none focus:ring-1 focus:ring-white/20 sm:h-16 sm:text-lg"
        />
      </div>

      {showResults && results.length === 0 ? (
        <p
          className="mt-4 text-sm text-zinc-500"
          data-testid="legal-search-empty"
          role="status"
        >
          Nenhuma seção encontrada.
        </p>
      ) : null}

      {showResults && results.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          className="mt-4 divide-y divide-zinc-800 overflow-hidden rounded-2xl border border-zinc-800 bg-[#0c0c0c]"
          data-testid="legal-search-results"
        >
          {results.map((result, index) => {
            const selected = index === activeIndex;

            return (
              <li key={result.id} role="presentation">
                <button
                  type="button"
                  id={`${listId}-${result.id}`}
                  role="option"
                  aria-selected={selected}
                  data-testid={`legal-search-result-${result.id}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectSection(result.id)}
                  className={cn(
                    "block w-full px-4 py-4 text-left transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/20 sm:px-5",
                    selected ? "bg-zinc-900/70" : "hover:bg-zinc-900/70",
                  )}
                >
                  <p className="text-sm font-medium text-white">{result.title}</p>
                  {result.snippet ? (
                    <p className="mt-1 line-clamp-2 text-sm text-zinc-400">
                      {result.snippet}
                    </p>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
